<template>
  <b-modal
    id="void-reason-modal"
    v-model="visible"
    no-close-on-backdrop
    hide-footer
    size="md"
    body-class="modal-height p-0"
    no-fade
    @hidden="onHidden"
  >
    <template slot="modal-header">
      <b-button
        variant="outline-light"
        @click="cancel"
      >
        {{ $t('voidreason.cancel') }}
      </b-button>
      <h5>{{ title }}</h5>
      <b-button
        variant="primary"
        @click="confirm"
      >
        {{ $t('voidreason.done') }}
      </b-button>
    </template>

    <div class="void-reason__options">
      <div
        v-for="reason in reasons"
        :key="reason.id"
        class="void-reason__option"
      >
        <label class="container">{{ reason.name }}
          <input
            v-model="selectedOption"
            :value="reason.name"
            type="radio"
          >
          <span class="checkmark" />
        </label>
      </div>

      <div class="void-reason__option">
        <label class="container">{{ $t('voidreason.custom') }}
          <input
            v-model="selectedOption"
            :value="CUSTOM"
            type="radio"
          >
          <span class="checkmark" />
        </label>
      </div>

      <div
        v-if="selectedOption === CUSTOM"
        class="void-reason__custom"
      >
        <b-form-input
          ref="customInput"
          v-model="customReason"
          :placeholder="$t('voidreason.custom-reason-placeholder')"
          :maxlength="MAX_REASON_LENGTH"
          @input="onCustomInput"
        />
        <div class="void-reason__counter">
          {{ customReason.length }}/{{ MAX_REASON_LENGTH }}
        </div>
      </div>

      <p
        v-if="showError"
        class="void-reason__error"
      >
        {{ $t("voidreason.reason-required") }}
      </p>
    </div>
  </b-modal>
</template>

<script>
import EventBus from "@/helpers/eventBus";

const CUSTOM = "__custom__";
const MAX_REASON_LENGTH = 100;

export default {
  name: "VoidReasonModal",
  data () {
    return {
      visible: false,
      CUSTOM,
      MAX_REASON_LENGTH,
      selectedOption: null,
      customReason: "",
      title: "",
      showError: false,
      resolver: null,
      resolved: false,
    };
  },
  computed: {
    reasons () {
      // Sourced from /api/config/ (persisted to IndexedDB) via the config store.
      return this.$store.getters["config/voidReasons"] || [];
    },
    finalReason () {
      if (this.selectedOption === CUSTOM) {
        return (this.customReason || "").trim();
      }
      return this.selectedOption || "";
    },
  },
  watch: {
    selectedOption (value) {
      this.showError = false;
      if (value === CUSTOM) {
        // Reveal and focus the custom field on the next tick.
        this.customReason = "";
        this.$nextTick(() => {
          if (this.$refs.customInput) this.$refs.customInput.focus();
        });
      }
    },
  },
  mounted () {
    EventBus.$on("void-reason:open", this.open);
  },
  beforeDestroy () {
    EventBus.$off("void-reason:open", this.open);
  },
  methods: {
    open (payload = {}) {
      this.resolver = typeof payload.resolve === "function" ? payload.resolve : null;
      this.resolved = false;
      this.selectedOption = null;
      this.customReason = "";
      this.showError = false;
      this.title = payload.title || this.$t("voidreason.void-reason");
      this.visible = true;
    },
    onCustomInput (value) {
      // Hard-cap the custom reason at 100 chars (guards against paste/edge cases
      // where the maxlength attribute might not apply).
      if (value && value.length > MAX_REASON_LENGTH) {
        this.customReason = value.slice(0, MAX_REASON_LENGTH);
      }
      this.showError = false;
    },
    confirm () {
      if (!this.finalReason) {
        this.showError = true;
        return;
      }
      this.settle(this.finalReason);
      this.visible = false;
    },
    cancel () {
      this.settle(null);
      this.visible = false;
    },
    onHidden () {
      // Covers ESC / programmatic hides — never leave the caller hanging.
      this.settle(null);
    },
    settle (value) {
      if (this.resolved) return;
      this.resolved = true;
      if (this.resolver) {
        this.resolver(value);
      }
      this.resolver = null;
    },
  },
};
</script>

<style scoped lang="scss">
.void-reason {
  &__options {
    display: flex;
    flex-direction: column;
  }

  &__option {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 1rem;
    line-height: 1.875rem;
    padding: 9px 16px 9px 16px;
    background-color: #ffffff;
    position: relative;

    label {
      margin-bottom: 0;
    }

    &:after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      height: 1px;
      background: #E0E0E0;
    }

    .container {
      display: block;
      position: relative;
      cursor: pointer;
      padding-right: 32px;
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;

      & input {
        position: absolute;
        opacity: 0;
        cursor: pointer;
      }
    }

    .checkmark {
      position: absolute;
      top: 0;
      right: 0;
      height: 20px;
      width: 20px;
      border-radius: 50%;
      border: 1px solid #616161;

      &:after {
        content: "";
        position: absolute;
        display: none;
      }
    }

    .container input:checked ~ .checkmark:after {
      display: block;
      border-color: #2196F3;
    }

    .container input:checked ~ .checkmark {
      border-color: #2196F3;
    }

    .container .checkmark:after {
      top: 4px;
      left: 4px;
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: #2196F3;
    }
  }

  &__custom {
    padding: 12px 16px;
  }

  &__counter {
    text-align: right;
    font-size: 11px;
    color: #888;
    margin-top: 2px;
  }

  &__error {
    color: #dc3545;
    margin: 0;
    padding: 8px 16px 0 16px;
  }
}
</style>
